// Package imports:
import React from 'react';
// Component imports:
import DisplayBox from '../../DisplayBox/DisplayBox';
import Table from '../../../ui-elements/Table/Table';
import Alert from '../../../ui-elements/Alert/Alert';
// Service imports:
// Type imports:
import { IEventReport, IKeldanApiResponse } from '../../../types/KeldanTypes';
import { IPropertySearchResult } from '../../../types/PropertyTypes';
import Button from '../../../ui-elements/Button/Button';
import { GET_KELDAN_API_URL } from '../../../services/config';
import { useBuyingProcess } from '../../../services/buyhook';
import { ErrorMessages } from '../../../services/errorMessages';

interface IProps {
    isExample?: boolean,
    report: IEventReport<IPropertySearchResult>,
    bondsAllowed?: boolean
}

interface IBuyFasteignaSkra {
    fastanr: string,
    bonds: boolean | null
    reference: string | null
}

const OverviewReportBody: React.FC<IProps> = ({
    isExample,
    report,
    bondsAllowed
}) => {
    const { tryPurchaseItem, tryToBuyItem, resetState, ModalsAndToasts } = useBuyingProcess(async (values: IBuyFasteignaSkra, modalReference, modalBonds) => {
        try {
            const { fastanr, bonds, reference } = values;
            const url = `${GET_KELDAN_API_URL()}/Property/PropertySearchFastanr`;
            const query = new URLSearchParams({
                term: fastanr,
                reference: reference ?? modalReference,
                bonds: (bonds === null) ? `${modalBonds}` : `${bonds}`
            });

            const response = await fetch(`${url}?${query.toString()}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                }
            });
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                    return;
                }
                const responseBody: IKeldanApiResponse<IPropertySearchResult> = await response.json();
                const { message } = responseBody;
                if (message === ErrorMessages.NoCard) {
                    resetState('no card')
                } else if (message) {
                    resetState({
                        type: 'alert',
                        headText: message
                    });
                } else {
                    resetState('bad request');
                }
            } else {
                resetState('response not ok');
            }
        } catch (e) {
            resetState('network error');
        }
    }, 'fasteignaskrá');
    
    return (
        <>
            <div className='property-page-body'>
                <DisplayBox
                    className='property-report-box'
                    grayBackground={isExample}
                >
                    <div className='italic-text'>
                        Skýrsla keypt: {report.eventDate}
                    </div>
                    <Table
                        tableSize="lg"
                        data={report.overview?.response?.properties?.properties || null}
                        columns={[{
                            title: 'Fastanúmer',
                            dataAccessor: ({property_number}) => property_number,
                            textAlign: 'left'
                        }, {
                            title: 'Heiti',
                            dataAccessor: () => report.overview.response?.properties?.address?.street,
                            textAlign: 'left'
                        }, {
                            title: 'Húsnúmer',
                            dataAccessor: () => report.overview.response?.properties?.address?.house_number,
                            textAlign: 'left'
                        }, {
                            title: 'Merking',
                            dataAccessor: ({units}) => units[0].tag,
                            textAlign: 'left'
                        }, {
                            title: 'Eigandi',
                            dataAccessor: ({owners}) => {
                                if (!owners) return '';
                                const filteredOwners = owners.filter(o => o.owner_type === 'F');
                                let s = '';
                                for (let i = 0; i < filteredOwners.length; i++) {
                                    s += `${s === '' ? '' : ' / '}${filteredOwners[i].name}`
                                }
                                return s;
                            },
                            textAlign: 'left'
                        }, {
                            title: 'Sveitarfélag',
                            dataAccessor: () => report.overview.response?.properties?.address?.county,
                            textAlign: 'left'
                        }, {
                            title: 'Byggð',
                            dataAccessor: () => report.overview.response?.properties?.address?.city,
                            textAlign: 'left'
                        }, {
                            title: 'Fasteignaskrá',
                            dataAccessor: ({property_number}) => (
                                <Button
                                    buttonType="buy"
                                    size='sm'
                                    showLoader={(
                                        tryToBuyItem !== null
                                        && tryToBuyItem.fastanr === property_number
                                    )}
                                    onClick={() => property_number && tryPurchaseItem({
                                        fastanr: property_number,
                                        bonds: null,
                                        reference: null
                                    }, bondsAllowed
                                        ? 'confirm-modal-with-reference-and-bonds'
                                        : 'confirm-modal-with-reference')}
                                >
                                    Kaupa
                                </Button>
                            )
                        }]}
                    />
                </DisplayBox>
                <ModalsAndToasts />
            </div>
            <Alert
                type='info'
                headText='Athugið' 
                text={<>
                    <p>Birtar upplýsingar eru sóttar með tengingu við Fasteignaskrá Íslands. Allar upplýsingar eru veittar samkvæmt starfsreglum Fasteignaskrár og í samræmi við lög og reglur um persónuvernd og meðferð persónuupplýsinga.</p>
                    <p>Reynt er að tryggja að upplýsingar séu réttar en Keldan getur ekki ábyrgst áreiðanleika þeirra.</p>
                </>}
            />
        </>
    );
}

export default OverviewReportBody;